import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuthStore, isClientPortalRole } from '../../store/authStore';
import { authAPI, portalPublicAPI } from '../../api';
import { isReservedPortalSlug } from '../../lib/portalReservedSlugs';

export default function PortalLogon() {
  const { portalSlug: slugParam } = useParams();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const existingUser = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const portalSlug = (slugParam || '').trim();
  const invalidSlug = isReservedPortalSlug(portalSlug);

  const [branding, setBranding] = useState(null);
  const [brandingError, setBrandingError] = useState('');
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    if (invalidSlug) return;
    let cancelled = false;
    setBrandingError('');
    portalPublicAPI
      .getBranding(portalSlug)
      .then((data) => {
        if (!cancelled) setBranding(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setBrandingError(err.message || 'This client portal could not be found.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [portalSlug, invalidSlug]);

  useEffect(() => {
    if (isAuthenticated && isClientPortalRole(existingUser?.role) && existingUser?.onboardingCompletedAt) {
      navigate('/client/dashboard', { replace: true });
    } else if (isAuthenticated && isClientPortalRole(existingUser?.role) && !existingUser?.onboardingCompletedAt) {
      navigate('/client/onboarding', { replace: true });
    }
  }, [isAuthenticated, existingUser, navigate]);

  const primary = branding?.primaryColor || '#3B82F6';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await authAPI.portalLogin({
        portalSlug,
        email: form.email.trim(),
        password: form.password,
      });
      login(data.user, data.token);
      if (data.needsOnboarding) {
        navigate('/client/onboarding', { replace: true });
      } else {
        navigate('/client/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  if (invalidSlug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <p className="text-gray-600 dark:text-gray-300">Page not found.</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{
        background: `linear-gradient(160deg, ${primary}12 0%, var(--background, #0a0a0a) 45%, var(--background, #111827) 100%)`,
      }}
    >
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          {branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt=""
              className="mx-auto h-16 sm:h-20 w-auto object-contain mb-3"
            />
          ) : (
            <img
              src="/sparxmate-logo.png"
              alt="SparxMate"
              className="mx-auto h-14 w-auto object-contain opacity-90 mb-3"
            />
          )}
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {branding?.companyName ? `${branding.companyName}` : 'Client portal'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Sign in to view your documents</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200/80 dark:border-gray-700 p-8">
          {brandingError && (
            <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-100">
              {brandingError}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="portal-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                id="portal-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                disabled={loading || !!brandingError}
              />
            </div>
            <div>
              <label htmlFor="portal-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <input
                id="portal-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                disabled={loading || !!brandingError}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !!brandingError}
              className="w-full py-2.5 px-4 rounded-md text-white text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: primary }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <p className="text-center text-sm">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                disabled={!!brandingError}
                className="text-gray-600 dark:text-gray-400 hover:underline disabled:opacity-50"
              >
                Forgot password?
              </button>
            </p>
          </form>
        </div>

        <p className="text-center text-xs text-gray-500 dark:text-gray-500">
          Trade account?{' '}
          <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:underline">
            Staff sign in
          </Link>
        </p>
      </div>

      {showForgotPassword && (
        <PortalForgotPasswordModal
          portalSlug={portalSlug}
          primaryColor={primary}
          onClose={() => setShowForgotPassword(false)}
        />
      )}
    </div>
  );
}

function PortalForgotPasswordModal({ portalSlug, primaryColor, onClose }) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await portalPublicAPI.forgotPassword({
        portalSlug,
        email: email.trim(),
      });
      setSuccess(true);
      if (response.devResetUrl) {
        setDevResetUrl(response.devResetUrl);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not send reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reset portal password</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Enter the email you use for this client portal. If it matches an account here, we will email you a link to
              set a new password.
            </p>
            {error && (
              <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-200">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="portal-forgot-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                id="portal-forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                placeholder="you@example.com"
                disabled={isLoading}
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-2 rounded-md text-white text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {isLoading ? 'Sending…' : 'Send link'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-900 dark:text-green-100">
              If an account with that email exists for this portal, you will receive a reset link shortly. Check your
              inbox and spam folder.
            </div>
            {devResetUrl && (
              <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs break-all">
                <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Development — reset URL</p>
                <a href={devResetUrl} className="text-blue-600 dark:text-blue-300 hover:underline" target="_blank" rel="noreferrer">
                  {devResetUrl}
                </a>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 rounded-md text-white text-sm font-medium"
              style={{ backgroundColor: primaryColor }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
